import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import {
  computeDelta,
  formatDateTime,
  formatPercent,
  formatRatingOver5,
  getFinalRatingOver5,
  getLatestRecommendation,
  getRecommendationStatusColor,
  getStatusColor,
  titleCase,
} from "../report.utils";

function SmallCellText({ children, weight = 500 }) {
  return (
    <Typography
      variant="body2"
      sx={{
        width: "100%",
        fontWeight: weight,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      title={String(children || "")}
    >
      {children}
    </Typography>
  );
}

function PercentPill({ value }) {
  const v = Number(value || 0);
  const safe = Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.45 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formatPercent(value, 1)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={safe}
        sx={{
          height: 7,
          borderRadius: 8,
          bgcolor: "rgba(25,118,210,0.14)",
          "& .MuiLinearProgress-bar": { borderRadius: 8 },
        }}
      />
    </Box>
  );
}

function RecommendationCell({ row }) {
  const latest = getLatestRecommendation(row);
  if (!latest) return <Chip size="small" variant="outlined" label="None" />;

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ py: 0.4 }}>
      <Chip size="small" label={titleCase(latest.action_type)} />
      <Chip
        size="small"
        label={titleCase(latest.status)}
        color={getRecommendationStatusColor(latest.status)}
        variant="outlined"
      />
    </Stack>
  );
}

function DeltaCell({ row }) {
  const previous = row?.previous_cycle?.final_percentage;
  const delta = computeDelta(row);

  if (previous === null || previous === undefined) return <Typography variant="body2">-</Typography>;

  const deltaLabel = delta === null ? "-" : `${delta > 0 ? "+" : ""}${delta?.toFixed?.(1) || 0}`;
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{formatPercent(previous, 1)}</Typography>
      <Chip
        size="small"
        label={deltaLabel}
        color={delta > 0 ? "success" : delta < 0 ? "error" : "default"}
        variant="outlined"
      />
    </Stack>
  );
}

function RowActionsMenu({ row, canFinalize, onOpenDetails, onOpenFinalize }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onOpenDetails(row);
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>

        {canFinalize ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onOpenFinalize(row);
            }}
          >
            <TaskAltIcon fontSize="small" sx={{ mr: 1 }} />
            Finalize Action
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

function ReportGridToolbar({ search, onSearchChange, selectedCount, rowCount, onRetry, isError }) {
  return (
    <GridToolbarContainer
      sx={{
        px: 1.25,
        py: 0.75,
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.88),
        backdropFilter: "blur(6px)",
      }}
    >
      <TextField
        size="small"
        label="Quick filter"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          minWidth: { xs: "100%", sm: 300 },
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            bgcolor: "rgba(255,255,255,0.95)",
          },
        }}
      />

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label={`Rows: ${rowCount || 0}`} variant="outlined" sx={{ borderRadius: "8px" }} />

        {selectedCount > 0 ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${selectedCount} selected`}
            sx={{ borderRadius: "8px" }}
          />
        ) : null}

        {isError ? (
          <IconButton onClick={onRetry} size="small" aria-label="Retry">
            <RefreshIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </GridToolbarContainer>
  );
}

const getFallbackRowId = (row) => {
  if (row?.id !== undefined && row?.id !== null) return row.id;

  const code = row?.employee?.employee_code || "emp";
  const cycle = row?.cycle_name || "cycle";
  const project = row?.project?.id || row?.project?.name || "project";
  const ts = row?.last_updated_at || row?.updated_at || "na";
  return `${code}-${cycle}-${project}-${ts}`;
};

export default function ReportDataGrid({
  rows,
  rowCount,
  loading,
  errorMessage,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  selectionModel,
  onSelectionModelChange,
  search,
  onSearchChange,
  onRetry,
  canFinalize,
  onOpenDetails,
  onOpenFinalize,
}) {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const downMd = useMediaQuery(theme.breakpoints.down("md"));
  const downLg = useMediaQuery(theme.breakpoints.down("lg"));
  const reportRowHeight = downSm ? 68 : downMd ? 74 : 78;

  const selectedCount = Array.isArray(selectionModel)
    ? selectionModel.length
    : selectionModel?.ids instanceof Set
    ? selectionModel.ids.size
    : Array.isArray(selectionModel?.ids)
    ? selectionModel.ids.length
    : 0;

  const columns = useMemo(
    () => [
      {
        field: "employee_code",
        headerName: "Code",
        minWidth: downSm ? 84 : 94,
        flex: downSm ? 0.64 : 0.72,
        sortable: true,
        valueGetter: (_v, row) => row?.employee?.employee_code || "-",
        renderCell: ({ value }) => <SmallCellText>{value}</SmallCellText>,
      },
      {
        field: "employee_name",
        headerName: "Name",
        minWidth: downSm ? 102 : 132,
        flex: 1,
        sortable: true,
        valueGetter: (_v, row) => row?.employee?.name || "-",
        renderCell: ({ value }) => <SmallCellText weight={600}>{value}</SmallCellText>,
      },
      {
        field: "department",
        headerName: "Dept",
        minWidth: 105,
        flex: 0.75,
        valueGetter: (_v, row) => row?.employee?.department || "-",
      },
      {
        field: "position",
        headerName: "Position",
        minWidth: 125,
        flex: 0.95,
        valueGetter: (_v, row) => row?.employee?.position || "-",
        renderCell: ({ value }) => <SmallCellText>{value}</SmallCellText>,
      },
      {
        field: "project",
        headerName: "Project",
        minWidth: 120,
        flex: 0.9,
        valueGetter: (_v, row) => row?.project?.name || "-",
        renderCell: ({ value }) => <SmallCellText>{value}</SmallCellText>,
      },
      {
        field: "cycle_name",
        headerName: "Cycle",
        minWidth: 115,
        flex: 0.85,
        renderCell: ({ value }) => <SmallCellText>{value || "-"}</SmallCellText>,
      },
      {
        field: "final_percentage",
        headerName: "Final %",
        minWidth: downSm ? 98 : 132,
        flex: downSm ? 0.8 : 0.86,
        sortable: true,
        renderCell: ({ row }) => <PercentPill value={row?.final_percentage} />,
      },
      {
        field: "final_score_5",
        headerName: "Rate /5",
        minWidth: 88,
        flex: 0.62,
        sortable: true,
        valueGetter: (_v, row) => getFinalRatingOver5(row),
        renderCell: ({ row }) => (
          <Typography variant="body2">{formatRatingOver5(getFinalRatingOver5(row))}</Typography>
        ),
      },
      {
        field: "previous_delta",
        headerName: "Prev + Delta",
        minWidth: 118,
        flex: 0.85,
        sortable: false,
        renderCell: ({ row }) => <DeltaCell row={row} />,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: downSm ? 82 : 98,
        flex: downSm ? 0.66 : 0.65,
        sortable: true,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={titleCase(value)}
            color={getStatusColor(value)}
            variant="outlined"
          />
        ),
      },
      {
        field: "latest_recommendation",
        headerName: "Latest Recommendation",
        minWidth: downSm ? 130 : 188,
        flex: downSm ? 0.92 : 1.15,
        sortable: false,
        renderCell: ({ row }) => <RecommendationCell row={row} />,
      },
      {
        field: "last_step",
        headerName: "Last Step",
        minWidth: 126,
        flex: 0.94,
        renderCell: ({ value }) => <SmallCellText>{value || "-"}</SmallCellText>,
      },
      {
        field: "last_updated_at",
        headerName: "Updated",
        minWidth: 138,
        flex: 1,
        sortable: true,
        valueGetter: (_value, row) => row?.last_updated_at || row?.updated_at || null,
        renderCell: ({ value }) => <Typography variant="body2">{formatDateTime(value)}</Typography>,
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: downSm ? 56 : 64,
        align: "center",
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <RowActionsMenu
            row={row}
            canFinalize={canFinalize}
            onOpenDetails={onOpenDetails}
            onOpenFinalize={onOpenFinalize}
          />
        ),
      },
    ],
    [canFinalize, downSm, onOpenDetails, onOpenFinalize]
  );

  const columnVisibilityModel = useMemo(() => {
    const model = {
      employee_code: true,
      employee_name: true,
      department: true,
      position: true,
      project: true,
      cycle_name: true,
      final_percentage: true,
      final_score_5: true,
      previous_delta: true,
      status: true,
      latest_recommendation: true,
      last_step: true,
      last_updated_at: true,
      actions: true,
    };

    if (downLg) {
      model.position = false;
      model.last_step = false;
    }
    if (downMd) {
      model.department = false;
      model.project = false;
      model.cycle_name = false;
      model.last_updated_at = false;
    }
    if (downSm) {
      model.employee_code = false;
      model.final_score_5 = false;
      model.previous_delta = false;
      model.latest_recommendation = false;
    }

    return model;
  }, [downSm, downMd, downLg]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        maxWidth: "100%",
        borderColor: theme.hr?.line?.soft || alpha(theme.palette.primary.main, 0.16),
        background: alpha(theme.palette.background.paper, 0.96),
      }}
    >
      {errorMessage ? (
        <Alert
          severity="error"
          action={
            <IconButton color="inherit" size="small" onClick={onRetry}>
              <RefreshIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {errorMessage}
        </Alert>
      ) : null}

      <DataGrid
        rows={rows}
        getRowId={getFallbackRowId}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        checkboxSelection={!downSm}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        density={downMd ? "compact" : "standard"}
        rowHeight={reportRowHeight}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        slots={{ toolbar: ReportGridToolbar }}
        slotProps={{
          toolbar: {
            search,
            onSearchChange,
            selectedCount,
            rowCount,
            onRetry,
            isError: Boolean(errorMessage),
          },
        }}
        sx={{
          minHeight: 480,
          width: "100%",
          border: 0,
          bgcolor: "transparent",
          "& .MuiDataGrid-toolbarContainer": {
            position: "sticky",
            top: 0,
            zIndex: 2,
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: alpha(theme.palette.primary.main, 0.11),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            backdropFilter: "blur(3px)",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 800,
            fontSize: 12.5,
          },
          "& .MuiDataGrid-cell": {
            py: 0.8,
            alignItems: "center",
            borderColor: alpha(theme.palette.text.primary, 0.06),
          },
          "& .MuiDataGrid-row.row-even": {
            bgcolor: alpha(theme.palette.primary.main, 0.012),
          },
          "& .MuiDataGrid-row.row-odd": {
            bgcolor: alpha(theme.palette.background.paper, 0.92),
          },
          "& .MuiDataGrid-main": {
            overflowX: "hidden",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden !important",
          },
          "& .MuiDataGrid-row:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.065),
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
        }
      />
    </Paper>
  );
}
